import type { ChoiceQuestion } from './polymersBiomolecules';

// Reviewed food-chemistry and enzyme questions for the `food-enzymes` mode. Data meta and sources
// are declared in `FOOD_DATA_META` (polymersBiomoleculesFood.ts).

export const FOOD_QUESTIONS: ChoiceQuestion[] = [
  {
    id: 'denaturation-change',
    prompt: 'An enzyme is heated to 70 °C and denatures. What has changed?',
    options: [
      {
        id: 'shape',
        label: 'Its tertiary structure, so the active site loses its shape',
        correct: true,
        feedback:
          'Correct. Heat breaks hydrogen bonds, ionic attractions and other forces between R groups. The chain unfolds and the active site no longer fits the substrate. The primary structure is unchanged.',
      },
      {
        id: 'peptide-bonds',
        label: 'Its peptide bonds have broken into single amino acids',
        correct: false,
        feedback:
          'Peptide bonds are strong covalent bonds and survive denaturation. Breaking them is hydrolysis, which needs water and usually another enzyme or strong acid.',
      },
      {
        id: 'killed',
        label: 'The enzyme has been killed',
        correct: false,
        feedback:
          'Enzymes are protein molecules, not living things, so they cannot be killed. They lose their shape, and with it their activity.',
      },
      {
        id: 'sequence',
        label: 'Its amino acid sequence has been rearranged',
        correct: false,
        feedback:
          'The sequence (primary structure) stays the same. Only the folding, held by weaker forces, is disrupted.',
      },
    ],
  },
  {
    id: 'cold-enzyme',
    prompt: 'Amylase works slowly at 5 °C. What happens when it is warmed back to 37 °C?',
    options: [
      {
        id: 'recovers',
        label: 'It works normally again',
        correct: true,
        feedback:
          'Correct. Cold slows collisions but does not change the enzyme’s shape, so activity returns on warming. This is one reason refrigeration slows food spoilage without destroying enzymes.',
      },
      {
        id: 'denatured',
        label: 'It stays inactive because cold denatured it',
        correct: false,
        feedback:
          'Low temperature does not normally denature enzymes; it only reduces how often and how hard particles collide. Activity returns when warmed.',
      },
      {
        id: 'faster',
        label: 'It works faster than before it was cooled',
        correct: false,
        feedback:
          'Cooling does not improve an enzyme. Back at 37 °C it returns to the same activity it had before.',
      },
    ],
  },
  {
    id: 'pepsin-intestine',
    prompt:
      'Why does pepsin stop working when stomach contents reach the small intestine (about pH 8)?',
    options: [
      {
        id: 'charges',
        label: 'The alkaline pH changes charges on R groups, so the active site changes shape',
        correct: true,
        feedback:
          'Correct. Pepsin’s optimum is about pH 2. At pH 8, acidic R groups lose H⁺, ionic and hydrogen bonds change and the active site no longer fits proteins.',
      },
      {
        id: 'no-protein',
        label: 'There is no protein left to digest',
        correct: false,
        feedback:
          'Plenty of protein reaches the small intestine, which is why trypsin and peptidases are needed there. Pepsin stops because the pH is wrong for it.',
      },
      {
        id: 'temperature',
        label: 'The small intestine is much hotter than the stomach',
        correct: false,
        feedback:
          'Body temperature is about the same throughout the gut. The big change is pH: from about 2 in the stomach to about 8 in the small intestine.',
      },
    ],
  },
  {
    id: 'digestion-type',
    prompt: 'Which reaction type describes the digestion of starch, proteins and fats?',
    options: [
      {
        id: 'hydrolysis',
        label: 'Hydrolysis: water breaks links between monomers',
        correct: true,
        feedback:
          'Correct. Digestion is the reverse of condensation polymerisation: each link takes up one H₂O and the monomers are released.',
      },
      {
        id: 'condensation',
        label: 'Condensation: monomers join and release water',
        correct: false,
        feedback:
          'Condensation builds the polymers in the first place. Digestion runs the other way, using water to break the links.',
      },
      {
        id: 'combustion',
        label: 'Combustion: food reacts with oxygen',
        correct: false,
        feedback:
          'Digestion does not use oxygen. Cellular respiration later oxidises the small molecules to release energy.',
      },
      {
        id: 'neutralisation',
        label: 'Neutralisation: acids react with bases',
        correct: false,
        feedback:
          'Stomach acid and pancreatic juice change the pH, but breaking food molecules into monomers is hydrolysis.',
      },
    ],
  },
  {
    id: 'specificity',
    prompt: 'Why does amylase digest starch but not protein?',
    options: [
      {
        id: 'active-site',
        label: 'Its active site fits only the glycosidic links in starch',
        correct: true,
        feedback:
          'Correct. An enzyme is specific: the shape and charges of its active site match one kind of substrate and link.',
      },
      {
        id: 'too-big',
        label: 'Protein molecules are too big for any enzyme',
        correct: false,
        feedback:
          'Pepsin and trypsin digest large proteins. The issue is fit, not size: the active site of amylase does not match a peptide link.',
      },
      {
        id: 'time',
        label: 'It would digest protein too, given enough time',
        correct: false,
        feedback:
          'More time does not help. Without a matching active site, amylase does not lower the activation energy for breaking peptide links.',
      },
    ],
  },
  {
    id: 'cellulose',
    prompt: 'Cellulose is a glucose polymer, yet humans cannot digest it. Why?',
    options: [
      {
        id: 'beta-links',
        label: 'Humans have no enzyme whose active site fits β-glucose links',
        correct: true,
        feedback:
          'Correct. Starch is built from α-glucose and cellulose from β-glucose. Human amylase fits only the α links, so cellulose passes through as dietary fibre.',
      },
      {
        id: 'no-glucose',
        label: 'Cellulose does not contain glucose',
        correct: false,
        feedback:
          'Cellulose is made entirely of glucose units. The difference from starch is the β link geometry.',
      },
      {
        id: 'peptide',
        label: 'Cellulose is held together by peptide links',
        correct: false,
        feedback:
          'Peptide links join amino acids. Cellulose units are joined by glycosidic links, but in the β form that human enzymes cannot break.',
      },
    ],
  },
  {
    id: 'fat-energy',
    prompt: 'Why does fat supply more energy per gram (37 kJ) than carbohydrate (16 kJ)?',
    options: [
      {
        id: 'less-oxidised',
        label: 'Fat has more C–H bonds and much less oxygen per gram',
        correct: true,
        feedback:
          'Correct. Carbohydrate is already partly oxidised (about one O per C). Fat is mostly hydrocarbon chain, so more oxygen reacts and more energy is released per gram when it is oxidised to CO₂ and H₂O.',
      },
      {
        id: 'denser',
        label: 'Fat is denser than carbohydrate',
        correct: false,
        feedback:
          'The factors are per gram, so density does not matter. The difference comes from composition: fat contains far less oxygen.',
      },
      {
        id: 'ester',
        label: 'Ester links store the energy',
        correct: false,
        feedback:
          'Breaking ester links (hydrolysis) releases very little energy. The energy comes from oxidising the long C–H chains.',
      },
    ],
  },
  {
    id: 'inhibitor-site',
    prompt: 'Where does a competitive inhibitor bind?',
    options: [
      {
        id: 'active-site',
        label: 'In the active site, in place of the substrate',
        correct: true,
        feedback:
          'Correct. Its shape is similar to the substrate, so it occupies the active site for a while and blocks the substrate.',
      },
      {
        id: 'elsewhere',
        label: 'Somewhere else on the enzyme, changing the active site',
        correct: false,
        feedback:
          'That describes non-competitive inhibition. A competitive inhibitor competes directly for the active site.',
      },
      {
        id: 'substrate',
        label: 'To the substrate, so the substrate cannot react',
        correct: false,
        feedback:
          'The inhibitor competes with the substrate for the enzyme. It binds to the active site of the enzyme, not to the substrate.',
      },
    ],
  },
  {
    id: 'label-serve',
    prompt: 'A label states 1200 kJ per 100 g. How much energy is in a 30 g serve?',
    options: [
      {
        id: 'scaled',
        label: '360 kJ',
        correct: true,
        feedback: 'Correct: 1200 kJ × 30 g ÷ 100 g = 360 kJ.',
      },
      {
        id: 'per-100',
        label: '1200 kJ',
        correct: false,
        feedback: '1200 kJ is for 100 g. A 30 g serve is 0.3 of that: 1200 × 30 ÷ 100 = 360 kJ.',
      },
      {
        id: 'inverted',
        label: '4000 kJ',
        correct: false,
        feedback:
          'That divided by 0.3 instead of multiplying. A smaller serve must contain less energy: 1200 × 0.3 = 360 kJ.',
      },
      {
        id: 'per-gram',
        label: '40 kJ',
        correct: false,
        feedback:
          '1200 ÷ 30 = 40 has no meaning here. Energy per gram is 12 kJ g⁻¹, and 30 g × 12 kJ g⁻¹ = 360 kJ.',
      },
    ],
  },
];
